import { PublicClientApplication, EventType } from '@azure/msal-browser';

// SAARA Frontend SPA app registration (multi-tenant). Real values land via
// REACT_APP_* env vars at build time; the placeholders let the dev server
// boot without throwing — the user just won't be able to sign in until the
// values are filled in.
export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '00000000-0000-0000-0000-000000000000',
    authority: 'https://login.microsoftonline.com/organizations',
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
  },
  cache: {
    // sessionStorage (per-tab, wiped on tab close) — NOT localStorage. With
    // localStorage the cache is shared across tabs and survives restarts, so a
    // single stale/corrupt entry after inactivity poisons every tab on every
    // load and never self-heals (the global white-page outage). sessionStorage
    // self-heals: a fresh tab always starts clean. The white-page-on-refresh
    // bug is handled by the resilient boot in index.js instead.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

// access_as_user is the standard delegated scope on the SAARA Backend API
// app registration (see Setup-EntraAppRegistrations.ps1).
export const apiScopes = [
  process.env.REACT_APP_API_SCOPE || 'api://saara-api/access_as_user',
];

export const msalInstance = new PublicClientApplication(msalConfig);

// Restore the active account on hot-reload so MSAL knows who the user is
// without forcing another sign-in roundtrip.
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
    msalInstance.setActiveAccount(event.payload.account);
  }
});

// NOTE: do NOT call getAllAccounts()/setActiveAccount() here. This module
// evaluates before msalInstance.initialize() runs in index.js, and touching the
// account cache pre-initialize against persisted state is fragile. The active
// account is restored by the LOGIN_SUCCESS callback above and by the post-init
// restore in index.js's boot().

/**
 * Restore the active account after initialize(). Safe to call only once MSAL is
 * initialized. Never throws.
 */
export function restoreActiveAccount() {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  } catch { /* ignore — boot renders regardless */ }
}

/**
 * Nuke ALL MSAL state from both web storages. MSAL credential/account entries
 * are NOT all prefixed with "msal." (many are keyed by homeAccountId-environment-
 * credentialType-...), so a prefix-only sweep leaves the poison behind. We match
 * the full set of markers MSAL uses. Used as the recovery path when boot detects
 * a corrupt/stuck cache. Preserves our own app keys (e.g. saara_active_tenant).
 */
export function purgeMsalStorage() {
  const looksLikeMsal = (k) =>
    k.startsWith('msal.') ||
    k.includes('login.windows.net') ||
    k.includes('login.microsoftonline.com') ||
    k.includes('.interaction.status') ||
    k.includes('-login.windows.net-') ||
    k.includes(msalConfig.auth.clientId) ||
    // credential entries: <homeAccountId>-<env>-<accesstoken|idtoken|refreshtoken>-...
    /-(accesstoken|idtoken|refreshtoken)-/i.test(k);
  [window.localStorage, window.sessionStorage].forEach((store) => {
    try {
      const keys = [];
      for (let i = 0; i < store.length; i += 1) {
        const k = store.key(i);
        if (k && looksLikeMsal(k)) keys.push(k);
      }
      keys.forEach((k) => store.removeItem(k));
    } catch { /* storage unavailable — nothing to purge */ }
  });
}

const ACTIVE_TENANT_KEY = 'saara_active_tenant';

export function getActiveTenantId() {
  try { return localStorage.getItem(ACTIVE_TENANT_KEY); }
  catch { return null; }
}

export function setActiveTenantId(tenantId) {
  try {
    if (tenantId) localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
    else localStorage.removeItem(ACTIVE_TENANT_KEY);
  } catch { /* ignore */ }
}

/**
 * Acquires an access token silently, falling back to a redirect login if the
 * silent path fails (no cached token, expired refresh token, etc).
 */
export async function acquireAccessToken() {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: apiScopes,
      account,
    });
    return result.accessToken;
  } catch (e) {
    // Caller decides whether to trigger an interactive login.
    return null;
  }
}

/**
 * Globally wraps window.fetch so that requests targeting the SAARA API base
 * receive the bearer token + active-tenant header automatically. Pages keep
 * calling plain `fetch(\`${API}/api/...\`)` without per-page changes.
 *
 * Idempotent — safe to call once at app boot.
 */
export function installApiFetchInterceptor(apiBaseUrl) {
  if (typeof window === 'undefined' || !window.fetch || window.__saaraFetchInstalled) return;
  const original = window.fetch.bind(window);
  window.__saaraFetchInstalled = true;

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input?.url || '');
    const isApiCall = url.startsWith(apiBaseUrl) || url.startsWith('/api/');

    if (!isApiCall) return original(input, init);

    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined));

    if (!headers.has('Authorization')) {
      const token = await acquireAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    if (!headers.has('X-Saara-Active-Tenant')) {
      const tenant = getActiveTenantId();
      if (tenant) headers.set('X-Saara-Active-Tenant', tenant);
    }

    const response = await original(input, { ...init, headers });

    // M2 tenant-lifecycle gates surface here. The middleware returns 402 for
    // Suspended and 410 for Cancelled; both are app-wide events the UI must
    // react to. We dispatch a custom event the App listens for, then return
    // the response so per-page error handling still runs.
    if (response.status === 402 || response.status === 410) {
      const evt = response.status === 402 ? 'saara:tenant-suspended' : 'saara:tenant-cancelled';
      try { window.dispatchEvent(new CustomEvent(evt)); } catch { /* SSR-safe */ }
    }

    return response;
  };
}
